import { useCallback, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import type { OfferListAndAction, OfferListV2Item } from 'components/OfferList/OfferList'
import OfferList, { OfferListAction } from 'components/OfferList/OfferList'
import { getUrlOfferListFilters } from 'selectors/routerSelectors'
import type { ConnectedProps } from 'react-redux'
import { connect } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import clsx from 'clsx'
import LayoutContainer from 'components/Common/LayoutContainer'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import {
  deleteSearchParams,
  parseSearchString,
  setSearchParamValue,
  setManySearchParamValues,
} from 'lib/url/searchUrlUtils'
import type { EnabledFilters } from 'components/Search/type'
import config from 'constants/config'
import type {
  FilterControlChangeHandler,
} from 'components/Pages/HotelSearchPage/HotelSearchPageFilterPane'
import HotelSearchPageFilterPane from 'components/Pages/HotelSearchPage/HotelSearchPageFilterPane'
import { ANYWHERE_NAME, ANYWHERE_PLACE_ID } from 'constants/search'
import { getSearchKey, getSearchTypeFromFilters } from 'lib/search/searchUtils'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useGlobalSearchURLHashVertical from 'hooks/GlobalSearch/useGlobalSearchURLHashVertical'
import { useScreenSizeOnly, useScreenSizeUp } from 'hooks/useScreenSize'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { replace } from 'lib/routing/appRouting'
import type { SearchFormRef } from 'components/Search'
import useOfferList from 'hooks/Offers/useOfferList'
import useOfferListSortOptions from 'hooks/useOfferListSortOptions'
import AnalyticsPageContext, { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import { getAppliedFilterCount } from 'components/Search/utils'
import HotelSearchNoSearchResultSection from 'components/Pages/HotelSearchPage/HotelSearchNoSearchResultSection'
import OfferCountAndSort from 'components/Pages/HotelSearchPage/OfferCountAndSort'
import OfferRecommendations from 'components/Common/OfferRecommendations/OfferRecommendations'
import useQueryParams from 'hooks/useQueryParams'
import { GLOBAL_SEARCH_INITIAL_STATE } from 'contexts/GlobalSearch/GlobalSearchState'
import HotelSearchHeader from 'components/Pages/HotelSearchPage/HotelSearchHeader'
import { useGlobalHotelsSearchContext } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalHotelsSearch'
import GlobalSearchContextProvider from 'contexts/GlobalSearch/GlobalSearchContextProvider'
import { SEARCH_STREAMING_PAGE_SIZE } from 'search/initialiseSearchSocket'
import type { SortControlChangeHandler } from 'components/Common/OfferSortControl/OfferSortControl'
import { setSearchEvent } from 'actions/SearchActions'
import { pushWithRegion } from 'actions/NavigationActions'

const OVERRIDE_PAGE_SIZE = 10
// Campaign landing pages must surface the full set of tagged offers, but the unified
// search endpoint returns only a small default page when no limit is supplied — so a
// campaign like Dreamworld showed ~10 of 29 offers. Force a high limit for campaign
// offer lists so the whole set is fetched (client-side pagination then handles display).
// An explicit `?limit=` in the URL still takes precedence.
const CAMPAIGN_OFFER_LIST_LIMIT = 50

// Offer types that have been retired from the app but may still be present in older
// Contentful configs (last_minute_hotel was removed in #35339). An unrecognised type
// disqualifies the list from the hotel-specific search light endpoint and silently
// falls back to the unified endpoint, so they must be stripped before building filters.
const RETIRED_OFFER_TYPES = new Set(['last_minute_hotel'])

/**
 * Strips retired offer types from a Contentful-configured offer type list.
 * Coalesces to undefined when every type was retired — an empty array vacuously
 * passes `isHotelSpecificRequest` ([].every is true) and would hit the search
 * light endpoint with no type constraint at all (same convention as getOfferList).
 */
export function sanitizeOfferTypes(
  offerTypes: Array<App.OfferListOfferType> | undefined,
): Array<App.OfferListOfferType> | undefined {
  const sanitized = offerTypes?.filter(type => !RETIRED_OFFER_TYPES.has(type))
  return sanitized?.length ? sanitized : undefined
}

/**
 * Builds the offer list filters for a Contentful vertical offer list, applying the
 * retired-offer-type sanitisation and the campaign limit (the unified list endpoint
 * defaults to 10 results when no limit is sent — see CAMPAIGN_OFFER_LIST_LIMIT).
 * A limit from the URL (`?limit=`) always takes precedence.
 */
export function buildListFilters({ filters, campaigns, offerTypes, placeIds, defaultRooms }: {
  filters: App.OfferListFilters
  campaigns: Array<string>
  offerTypes: Array<App.OfferListOfferType> | undefined
  placeIds: Array<string> | undefined
  defaultRooms: Array<App.Occupants>
}): App.OfferListFilters {
  const placeId = (placeIds && placeIds.length > 0) ? placeIds[0] : ANYWHERE_PLACE_ID
  return {
    ...filters,
    campaigns,
    offerTypes: sanitizeOfferTypes(offerTypes),
    rooms: filters.rooms?.length ? filters.rooms : defaultRooms,
    destinationId: filters.destinationId ? filters.destinationId : placeId,
    // Ensure campaign pages fetch the whole tagged set; an explicit URL limit still wins.
    ...(campaigns?.length && !filters.limit ? { limit: CAMPAIGN_OFFER_LIST_LIMIT } : {}),
  }
}

const REDIRECTION_PARAMS_MAP: Record<string, Array<{ paramName: string, paramValue: string }>> = {
  '/campaign/wintersun2': [
    { paramName: 'destinationName', paramValue: 'Asia' },
    { paramName: 'destinationId', paramValue: 'le_fdbf7de11c1a054dc927adf66d9b5fce' },
  ],
  '/campaign/wintersun-test': [
    { paramName: 'destinationName', paramValue: 'Asia' },
    { paramName: 'destinationId', paramValue: 'le_fdbf7de11c1a054dc927adf66d9b5fce' },
  ],
}

function hasSearchTarget(params: Record<string, string | undefined>): boolean {
  return !!(
    (params.destinationId && params.destinationName) ||
    (params.landmarkId && params.landmarkName) ||
    (params.propertyId && params.propertyName) ||
    params.bounds ||
    (params.lat && params.lng) ||
    params.query
  )
}

/**
 * Build the navigation target for a filter/sort change on a Contentful vertical offer list.
 * The offer list is rendered inline on the current campaign path (e.g. /campaign/golf/australia),
 * so navigation must stay on that path — we return `basePath`, NOT a hardcoded `/search`. Redirecting
 * to `/search` bounced the user off the campaign page (and the search page then dropped the
 * `campaigns` filter). `basePath` is the region-stripped pathname (`pathname.slice(3)`); on the real
 * search page that is already `/search`, so this is correct there too.
 */
export function getOfferListSearchNavigation(
  basePath: string,
  currentSearch: string,
  campaigns: Array<string> | undefined,
): { path: string, search: string } {
  const parsedSearch = parseSearchString(currentSearch)
  const additionalParams = [{ paramName: 'adults', paramValue: 2 }, { paramName: 'children', paramValue: 'none' }]
  if (basePath in REDIRECTION_PARAMS_MAP) {
    additionalParams.push(...REDIRECTION_PARAMS_MAP[basePath])
  } else if (!hasSearchTarget(parsedSearch)) {
    additionalParams.push(
      { paramName: 'destinationName', paramValue: ANYWHERE_NAME },
      { paramName: 'destinationId', paramValue: ANYWHERE_PLACE_ID },
    )
  }
  if (campaigns?.length) {
    additionalParams.push({ paramName: 'campaigns', paramValue: campaigns.join(',') })
  }
  return { path: basePath, search: setManySearchParamValues(currentSearch, additionalParams) }
}

const StlyedPane = styled.div`
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  padding-top: ${rem(24)};

  ${mediaQueryUp.tablet} {
    padding-top: ${rem(32)};
  }

  &.fullWidth {
    background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
    padding-top: ${rem(16)};

    ${mediaQueryUp.tablet} {
      padding-top: ${rem(32)};
    }
  }
`

const StyledLayoutContainer = styled(LayoutContainer)`
  &.filterPane {
    background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
    display: grid;
    padding: ${rem(24)} 0;
    grid-template-columns: minmax(0, 1fr);
    grid-gap: ${rem(32)};

    ${mediaQueryUp.desktop} {
      grid-template: "offer-filters offer-list" auto / ${rem(278)} minmax(0, 1fr);
      padding-top: ${rem(12)};
    }
  }
  &.fullWidth {
    padding: 0;
  }
`

const StyledVerticalSpacer = styled(VerticalSpacer)`
  grid-area: offer-list;
`

const StyledHotelSearchPageFilterPane = styled(HotelSearchPageFilterPane)`
  grid-area: offer-filters;
`

function mapState(state: App.State) {
  const filters = getUrlOfferListFilters(state)

  return {
    windowSearch: state.router.location.search,
    filters,
    memberId: state.auth.account.memberId,
    searchResultMetadata: state.offer.searchResultMetadata,
    isFlexibleSearch: !!(filters?.flexibleMonths && filters?.flexibleNights),
    specificPlaces: state.destination.specificPlaces[filters.destinationId ?? ''],
  }
}

const connector = connect(mapState)

type PropsFromRedux = ConnectedProps<typeof connector>

interface Props extends PropsFromRedux, App.ComponentVerticalOfferList {
  showFilters: boolean
  title: string
  campaigns: Array<string>
  offerTypes: Array<App.OfferListOfferType>
  placeIds: Array<string>
}

function ContentfulVerticalOfferList({
  filters,
  campaigns,
  offerTypes,
  searchResultMetadata,
  windowSearch,
  isFlexibleSearch,
  specificPlaces,
  showFilters,
  showSearchHeader,
  placeIds,
  isInfiniteScroll,
  offerIds,
}: Props) {
  const hasOverrideOfferIds = !!offerIds?.length
  const liveOverrideOfferCount = useAppSelector(state => {
    if (!offerIds?.length) return 0
    return offerIds.filter(id => {
      if (state.offer.offersLoading[id] || (!state.offer.offers[id] && !state.offer.offerErrors[id])) return true
      return !state.offer.offerErrors[id] && !state.offer.offers[id]?.isSoldOut
    }).length
  })
  const intl = useIntl()
  const navigate = useNavigate()
  const { landmarkSearchSortByTypes, searchSortByTypes, SORT_OPTION_CUSTOMER_RATING } = useOfferListSortOptions()
  const pathname = useAppSelector((state) => state.router.location.pathname)
  const dispatch = useAppDispatch()
  const { toggleSearchVertical } = useGlobalSearchURLHashVertical()
  const isMobile = useScreenSizeOnly('mobile')
  const isDesktop = useScreenSizeUp('largeDesktop')
  const searchType = getSearchTypeFromFilters(filters)
  const params = useMemo(() => parseSearchString(windowSearch), [windowSearch])
  const mapViewURL = useMemo(() => `/search/map?${deleteSearchParams(windowSearch, 'sortBy')}`, [windowSearch])
  const pageSize = SEARCH_STREAMING_PAGE_SIZE

  const formRef = useRef<SearchFormRef>(null)

  const onBedbankSelectDatesPrompt = useCallback((offerId: string, offerType: App.OfferType, windowSearch: string) => {
    const paramsToSet = [{ paramName: 'offerType', paramValue: offerType }, { paramName: 'offerId', paramValue: offerId }]
    if (isMobile) {
      toggleSearchVertical('hotels', { withDateSelect: true, search: setManySearchParamValues(windowSearch, paramsToSet) })
    } else {
      navigate({ search: setManySearchParamValues(windowSearch, paramsToSet) }, { replace: true })
      formRef.current?.form.current.scrollIntoView()
      formRef.current?.openDatePickerMenu()
    }
  }, [isMobile, navigate, toggleSearchVertical])
  const queryParams = useQueryParams()
  const { globalHotelsSearchDispatch, globalHotelsSearchState } = useGlobalHotelsSearchContext({
    ...GLOBAL_SEARCH_INITIAL_STATE,
    searchType,
    onBedbankSelectDatesPrompt,
  }, queryParams)

  const {
    searchTargetLabel,
  } = useMemo(() => ({
    searchTargetLabel: params.landmarkName || params.destinationName || params.propertyName,
    offerDistanceFromSearchTarget: searchResultMetadata.distanceFromSearchTarget[params.landmarkId ?? params.destinationId ?? ''],
  }), [
    params.landmarkId,
    params.landmarkName,
    params.destinationId,
    params.destinationName,
    params.propertyName,
    searchResultMetadata.distanceFromSearchTarget,
  ])

  const luxPlusEnabled = useAppSelector(getIsLuxPlusEnabled)
  const enabledFilters: EnabledFilters = useMemo(() => (
    {
      inclusions: true,
      amenities: true,
      customerRating: true,
      priceFilter: config.ENABLE_HOTEL_SEARCH_PAGE_PRICE_FILTER,
      holidayTypes: searchTargetLabel !== ANYWHERE_NAME,
      propertyTypes: searchTargetLabel !== ANYWHERE_NAME,
      locations: true,
      offerTypes: config.ENABLE_VILLA_RENTALS,
      popularFilters: true,
      luxPlusFeatures: luxPlusEnabled,
      agentHubFeatures: config.agentHub.isEnabled,
    }
  ), [searchTargetLabel, luxPlusEnabled])

  const defaultRooms = useMemo((): Array<App.Occupants> => [
    { adults: 2 },
  ], [])

  const listFilters = useMemo(
    () => buildListFilters({ filters, campaigns, offerTypes, placeIds, defaultRooms }),
    [placeIds, filters, campaigns, offerTypes, defaultRooms],
  )

  const onPageChange = useCallback((page: number) => {
    dispatch(replace({ search: setSearchParamValue(windowSearch, 'pages', page) }))
    // eslint-disable-next-line
    }, [windowSearch])

  const hotelOfferList = useOfferList(listFilters, { disabled: hasOverrideOfferIds })

  const overrideOfferListAndAction = useMemo<OfferListAndAction | undefined>(() => {
    if (!hasOverrideOfferIds) return undefined
    return {
      action: OfferListAction.OVERRIDE,
      offerList: { offerIds: offerIds, fetching: false, error: null, key: 'contentful-override' },
    }
  }, [hasOverrideOfferIds, offerIds])

  const sortOptions = useMemo(() => {
    const options = searchType === 'landmark' ? landmarkSearchSortByTypes : searchSortByTypes

    return [...options, SORT_OPTION_CUSTOMER_RATING]
  }, [searchType, SORT_OPTION_CUSTOMER_RATING, landmarkSearchSortByTypes, searchSortByTypes])

  const handleFilterChange = useCallback<FilterControlChangeHandler>((params) => {
    const parsedSearch = parseSearchString(params.search)
    dispatch(setSearchEvent(
      getSearchKey(parsedSearch),
      { searchEvent: { type: 'filters' } },
    ))
    const { path, search } = getOfferListSearchNavigation(pathname.slice(3), params.search, campaigns)
    dispatch(pushWithRegion(path, search, undefined, params.hash))
  }, [dispatch, pathname, campaigns])

  const handleSortChange = useCallback<SortControlChangeHandler>((params) => {
    const parsedSearch = parseSearchString(params.search)
    dispatch(setSearchEvent(
      getSearchKey(parsedSearch),
      { searchEvent: { type: 'sort' } },
    ))
    const { path, search } = getOfferListSearchNavigation(pathname.slice(3), params.search, campaigns)
    dispatch(pushWithRegion(path, search, undefined))
  }, [dispatch, pathname, campaigns])

  const searchListTotal = searchType === 'property' || (!hotelOfferList.offerCount || filters.limit)
    ? hotelOfferList.offerIds.length
    : hotelOfferList.offerCount

  const mainListTotal = hasOverrideOfferIds ? liveOverrideOfferCount : searchListTotal

  const filterCount = useMemo(() => {
    return getAppliedFilterCount(listFilters, enabledFilters)
  }, [listFilters, enabledFilters])

  const hasFiltersApplied = filterCount > 0
  const mainListFetching = hotelOfferList.fetching
  const showFilterPane = showFilters && isDesktop && (mainListTotal > 0 || mainListFetching || hasFiltersApplied)

  const offerListFiltersProps = {
    sortOptions,
    searchType,
    switchView: {
      label: intl.formatMessage({
        id: 'tKP3X7BzeJthFuBwBgM9',
        defaultMessage: 'Map view',
        description: 'Label for the switch-to-map-view button on the contentful offer list page',
      }),
      view: 'map' as const,
      URL: mapViewURL,
    },
    listFilters,
  }

  const extras: Array<OfferListV2Item> = [{
    id: 'no-results',
    position: 'start',
    element: <HotelSearchNoSearchResultSection searchType={searchType} />,
    show: hasOverrideOfferIds ? mainListTotal === 0 : 'hasNoOffers',
  },
  {
    id: 'heading',
    position: 'start',
    element: <OfferCountAndSort
      destinationName={params.destinationName}
      specificPlaces={specificPlaces}
      fetching={mainListFetching}
      mainListTotal={mainListTotal}
      sortOptions={sortOptions}
      isFlexible={isFlexibleSearch}
      filters={listFilters}
      windowSearch={windowSearch}
      shouldShowFilters={showFilters && !showFilterPane}
      showHolidayTypes={searchTargetLabel !== ANYWHERE_NAME}
      searchType={searchType}
      enabledFilters={enabledFilters}
      onFilterChange={handleFilterChange}
      onSortChange={handleSortChange}
    />,
  },
  {
    id: 'recommendations',
    position: 'end',
    element: <OfferRecommendations useList trackingDescription="Search Results" />,
    show: searchType === 'property',
  },
  ]

  return (
    <AnalyticsPageContext.Provider value={AnalyticsPage.search}>
      <GlobalSearchContextProvider
        state={globalHotelsSearchState}
        dispatch={globalHotelsSearchDispatch}
      >
        {showSearchHeader && <HotelSearchHeader
          filters={listFilters}
          formRef={formRef}
          offerListFiltersProps={offerListFiltersProps}
          searchVertical="hotels"
          baseSearchPath={pathname.slice(3)}
          baseSearchMapPath="/search/map"
          onFilterChange={handleFilterChange}
          onSortChange={handleSortChange}
        />}

        <StlyedPane className={clsx({ fullWidth: isMobile })}>
          <StyledLayoutContainer size={showFilterPane ? 'xl' : 'lg'} className={clsx({ filterPane: showFilterPane, fullWidth: isMobile })}>
            <StyledVerticalSpacer gap={16}>
              <OfferList
                filters={hasOverrideOfferIds ? undefined : listFilters}
                extraOfferListAndAction={overrideOfferListAndAction}
                interstitials={extras}
                onPageChange={onPageChange}
                tileStyle="search"
                eagerLoadFirstImage
                pageSize={hasOverrideOfferIds ? OVERRIDE_PAGE_SIZE : pageSize}
                showLoadMoreTiles={!hasOverrideOfferIds || (offerIds?.length ?? 0) > OVERRIDE_PAGE_SIZE}
                defaultPage={params.pages ? parseInt(params.pages) : 1}
                fullWidth={isMobile}
                isInfiniteScroll={isInfiniteScroll}
              />
            </StyledVerticalSpacer>
            {showFilterPane && <StyledHotelSearchPageFilterPane
              filters={listFilters}
              mapViewURL="" // keep this empty to prevent the map view button from showing
              search={windowSearch}
              enabledFilters={enabledFilters}
              hotelOfferList={hotelOfferList}
              onFilterChange={handleFilterChange}
            />}
          </StyledLayoutContainer>
        </StlyedPane>
      </GlobalSearchContextProvider>
    </AnalyticsPageContext.Provider>
  )
}

export default connector(ContentfulVerticalOfferList)
